import Title from "@mgdx/ui/components/Title";
import clsx from "clsx";
import React, { Fragment, ReactNode } from "react";

import { Button, ButtonProps } from "../Button";
import { MessagePlain, Variant as MessagePlainVariant } from "../MessagePlain";
import ModalDialog, { Props as ModalDialogProps } from "../ModalDialog";
import * as styles from "./ConfirmDialog.module.css";

interface Text {
  text: string;
  variant?: MessagePlainVariant;
  isLeft?: boolean;
}

interface Button {
  text: string;
  props?: Omit<ButtonProps, "onClick" | "variant">;
}

export type Content = {
  title?: string;
  description?: Text;
  extraText?: Text;
  customText?: ReactNode;
  isVerticalButton?: boolean;
  confirmButton?: Button;
  cancelButton?: Button;
  customButtons?: ReactNode[];
  isReject?: boolean;
};

export type ConfirmDialogProps = Partial<ModalDialogProps> & {
  isOpen: boolean;
  dialogClassName?: string;
  overlayClassName?: string;
  content: Content;
  onCancel?(): void;
  onConfirm?(): void;
  onClose?(): void;
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  content,
  dialogClassName,
  overlayClassName,
  onCancel,
  onConfirm,
  onClose,
  ...props
}) => {
  const {
    title,
    description,
    extraText,
    customText,
    isVerticalButton,
    confirmButton,
    cancelButton,
    customButtons,
    isReject,
  } = content;

  return (
    <ModalDialog
      {...props}
      variant="confirm"
      className={clsx(styles.dialog, dialogClassName && dialogClassName)}
      overlayClassName={clsx(styles.overlay, overlayClassName)}
      isOpen={isOpen}
      onRequestClose={isReject ? onCancel : onClose}
    >
      {title && <Title variant="basic_center">{title}</Title>}
      {description && (
        <MessagePlain
          variant={description.variant ?? "info"}
          className={clsx(styles.message, description.isLeft && styles.left)}
        >
          {description.text}
        </MessagePlain>
      )}
      {extraText && (
        <MessagePlain
          variant={extraText.variant ?? "caution"}
          className={clsx(styles.message, extraText.isLeft && styles.left)}
        >
          {extraText.text}
        </MessagePlain>
      )}
      {customText}
      <div className="mt-4">
        <div className={clsx(styles.buttonGroup, isVerticalButton && styles.vertical)}>
          {confirmButton && (
            <Button variant="filled" color="key" onClick={onConfirm} data-testid="confirm-dialog-confirm-button">
              {confirmButton.text}
            </Button>
          )}
          {customButtons?.map((component, index) => (
            <Fragment key={index}>{component}</Fragment>
          ))}
          {cancelButton && (
            <Button variant="outlined" onClick={onCancel} data-testid="confirm-dialog-cancel-button">
              {cancelButton.text}
            </Button>
          )}
        </div>
      </div>
    </ModalDialog>
  );
};
